
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Calculator, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle,
  Clock, DollarSign, RefreshCw, Upload, FileText, X, Paperclip,
  ClipboardCheck, ExternalLink, RotateCcw, Users
} from 'lucide-react';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import { api } from '../../../../src/services/api';
import { supabase } from '../../../../src/lib/supabase';
import { logUsageEvent } from '../../../../src/lib/logger';
import { createPortal } from 'react-dom';
import {
  formatCurrency, formatKwh, formatPercent, getBonusVendedor,
  getComissaoPercentualVendedor, getTarifaVigente, isGrupoTarifarioA,
  calcularComissaoVendedor
} from '../../../../src/lib/comissaoUtils';
import { isCompetenciaNovaPolitica, MOSTRAR_ITENS_V2_EM_DISCUSSAO, calcularAtivacaoV2 } from '../../../../src/lib/politicaComercial';
import { calcularFechamentoExternoV2, montarRedesPorVendedor } from '../../../../src/lib/comissaoExternaV2';
import { calcularFechamentoInternoV1 } from '../../../../src/lib/comissaoInternaV1';
import { isCompetenciaNovaPoliticaInterno, calcularComissoesInterno } from '../../../../src/lib/comissaoInterna';
import { agruparRepassesPorPosVenda, POLITICA_POS_VENDA_INDICACAO } from '../../../../src/lib/comissaoIndicacaoPosVenda';
import type { RepassePosVenda } from '../../../../src/lib/comissaoIndicacaoPosVenda';
import { buscarPosVendaDasNegociacoes, IndicacoesIndisponiveisError } from '../../../../src/lib/comissaoIndicacaoPosVendaDb';
import { ehComercialInterno } from '../../../../src/lib/departamentos';
import type { ComissaoVendedor, ComissaoVendedorNegociacao, TarifaEnergia, Profile } from '../../../../types';

interface Props {
  comissoes: ComissaoVendedor[];
  vendedores: Profile[];
  metas: ComissaoVendedor[];
  tarifas: TarifaEnergia[];
  mes: number;
  ano: number;
  canEdit: boolean;
  todosUsuarios: Profile[];
  onRefresh: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  calculado: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  aprovado: 'bg-[#F6A937]/15 text-[#b85c00] dark:bg-[#F6A937]/10 dark:text-[#F6A937]',
  pago: 'bg-[#5F6C37]/10 text-[#3d4a22] dark:bg-[#5F6C37]/20 dark:text-[#84cc16]',
};

const VendedoresTab: React.FC<Props> = ({ comissoes, vendedores, metas, tarifas, mes, ano, canEdit, todosUsuarios, onRefresh }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detalhe, setDetalhe] = useState<ComissaoVendedorNegociacao[]>([]);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [autoCalcDone, setAutoCalcDone] = useState(false);
  const [autoCalcRunning, setAutoCalcRunning] = useState(false);
  // Comissão do pós-venda por indicação (#6236): sem ler as indicações, a Folha não recalcula.
  const [avisoIndicacoes, setAvisoIndicacoes] = useState<string | null>(null);

  // Payment confirmation modal state
  const [paymentModal, setPaymentModal] = useState<{ comissao: ComissaoVendedor } | null>(null);
  const [approvalModal, setApprovalModal] = useState<{ comissao: ComissaoVendedor } | null>(null);
  const [resetModal, setResetModal] = useState<{ comissao: ComissaoVendedor } | null>(null);
  const [resetting, setResetting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingApproval, setUploadingApproval] = useState(false);
  const [comprovanteFile, setComprovanteFile] = useState<File | null>(null);
  const [comprovantePreview, setComprovantePreview] = useState<string | null>(null);

  // ── Refs to break dependency loops ──
  const comissoesRef = useRef(comissoes);
  comissoesRef.current = comissoes;
  const metasRef = useRef(metas);
  metasRef.current = metas;
  const tarifasRef = useRef(tarifas);
  tarifasRef.current = tarifas;
  const vendedoresRef = useRef(vendedores);
  vendedoresRef.current = vendedores;
  const autoCalcRunningRef = useRef(false);

  // Stable fingerprint: detects when metas or tarifas change from ConfiguracoesTab
  const configFingerprint = useMemo(() => {
    const metasPart = metas.map(m => `${m.vendedor_id}:${m.meta_kwh}:${m.salario_base}`).sort().join('|');
    const tarifasPart = tarifas.map(t => `${t.id}:${t.valor}`).sort().join('|');
    return `${metasPart}__${tarifasPart}`;
  }, [metas, tarifas]);

  // Core calc logic — skipApproved=true for auto, false for manual sync, forceAll=true recalculates even paid
  const runCalc = useCallback(async (skipApproved: boolean, forceAll = false) => {
    if (autoCalcRunningRef.current) return;
    if (tarifasRef.current.length === 0) return;
    // GUARD: don't run if vendedores haven't loaded yet (prevents race condition on mount)
    if (vendedoresRef.current.length === 0) return;

    autoCalcRunningRef.current = true;
    setAutoCalcRunning(true);
    try {
      const mesStr = String(mes).padStart(2, '0');
      const mesInicio = `${ano}-${mesStr}-01T00:00:00`;
      const lastDay = new Date(ano, mes, 0).getDate();
      const mesFim = `${ano}-${mesStr}-${lastDay}T23:59:59`;

      const [negociacoes, spilloverResult] = await Promise.all([
        api.folhaPagamento.getNegociacoesAprovadas(mesInicio, mesFim),
        api.folhaPagamento.getNegociacoesSpilloverMesAnterior(mes, ano),
      ]);

      // Comissão do pós-venda por indicação (#6236): quem do pós-venda gerou cada venda.
      // Lido ANTES de qualquer gravação: se este usuário não consegue ler as indicações, a
      // Folha não recalcula nada — gravaria comissão cheia em venda que é dividida e zeraria
      // a parte do pós-venda.
      let posVendaPorNeg: Map<number, string>;
      try {
        posVendaPorNeg = (await buscarPosVendaDasNegociacoes([...negociacoes, ...spilloverResult.negs] as any[])).porNegociacao;
        setAvisoIndicacoes(null);
      } catch (e) {
        if (e instanceof IndicacoesIndisponiveisError) {
          setAvisoIndicacoes(e.message);
          return 'bloqueado' as const;
        }
        throw e;
      }

      // Para cada neg spillover: remover do mês anterior e recalcular total_kwh_vendido anterior
      if (spilloverResult.negs.length > 0) {
        // Agrupar spillover por vendedor
        const spilloverByVendedor: Record<string, number[]> = {};
        for (const n of spilloverResult.negs) {
          const vid = n.responsavel_id;
          if (!vid) continue;
          if (!spilloverByVendedor[vid]) spilloverByVendedor[vid] = [];
          spilloverByVendedor[vid].push(n.id);
        }
        for (const [vid, negIds] of Object.entries(spilloverByVendedor)) {
          const prev = spilloverResult.prevComissaoMap.get(vid);
          if (!prev) continue;
          // Remove do mês anterior
          await supabase.from('comissao_vendedor_negociacoes')
            .delete()
            .eq('comissao_vendedor_id', prev.id)
            .in('negociacao_id', negIds);
          // Recalcula total_kwh_vendido do mês anterior sem as negs removidas
          const { data: remaining } = await supabase
            .from('comissao_vendedor_negociacoes')
            .select('consumo_medio_kwh, is_grupo_a')
            .eq('comissao_vendedor_id', prev.id);
          const newTotalKwh = (remaining || [])
            .filter((r: any) => !r.is_grupo_a)
            .reduce((s: number, r: any) => s + (Number(r.consumo_medio_kwh) || 0), 0);
          await supabase.from('comissao_vendedores')
            .update({ total_kwh_vendido: newTotalKwh, updated_at: new Date().toISOString() })
            .eq('id', prev.id);
        }
      }

      // Merge: spillover entra no mês atual
      const seenIds = new Set(negociacoes.map((n: any) => n.id));
      const allNegociacoes = [...negociacoes, ...spilloverResult.negs.filter((n: any) => !seenIds.has(n.id))];
      // Venda indicada pelo pós-venda: os motores (interno e externo) dividem a comissão dela.
      allNegociacoes.forEach((n: any) => { n.pos_venda_id = posVendaPorNeg.get(Number(n.id)) ?? null; });
      const repassesPosVenda: RepassePosVenda[] = [];

      const tarifa = getTarifaVigente(tarifasRef.current, `${ano}-${mesStr}-01`);
      if (!tarifa) return;

      // ALWAYS fetch fresh comissoes for this month from DB to prevent stale ref issues
      const freshComissoes = await api.folhaPagamento.getComissoesVendedores(mes, ano);

      const byVendedor: Record<string, any[]> = {};
      
      // Initialize for current active vendedores
      vendedoresRef.current.forEach(v => {
        byVendedor[v.id] = [];
      });

      // Also initialize for people who already have commission records this month
      // (e.g., someone who was a vendedor but changed role mid-period)
      freshComissoes.forEach(c => {
        if (!byVendedor[c.vendedor_id]) {
          byVendedor[c.vendedor_id] = [];
        }
      });

      // Build a set of vendedor IDs we are tracking
      const vendedorIds = new Set(Object.keys(byVendedor));

      allNegociacoes.forEach((n: any) => {
        const vid = n.responsavel_id;
        // Include if the responsible person is a current vendedor OR has an existing commission record
        if (!vendedorIds.has(vid)) return;
        if (!byVendedor[vid]) byVendedor[vid] = [];
        const uc = n.unidades_consumidoras?.[0];
        const grupoA = isGrupoTarifarioA(uc?.grupo_tarifario);
        byVendedor[vid].push({ ...n, is_grupo_a: grupoA });
      });

      if (Object.keys(byVendedor).length === 0) return;


      // Always fetch previous month metas as fallback for meta/salary inheritance
      let previousMetas: Map<string, { meta_kwh: number; salario_base: number }> | null = null;
      try {
        previousMetas = await api.folhaPagamento.getLastMetasVendedores(mes, ano);
      } catch (e) {
        console.warn('Could not fetch previous metas:', e);
      }

      // Nova Política Comercial (competência >= jul/2026): motor v2
      const novaPolitica = isCompetenciaNovaPolitica(mes, ano);
      const redesPorVendedor = novaPolitica
        ? await montarRedesPorVendedor(allNegociacoes as any)
        : new Map();

      // Perfil por vendedor_id — decide quem passa pelo motor do Comercial
      // Interno. Inclui quem já saiu do roster ativo mas tem comissão gravada
      // no mês (mesmo motivo do byVendedor logo acima: preservar histórico).
      const perfilPorVendedorId = new Map<string, Profile>();
      vendedoresRef.current.forEach(v => perfilPorVendedorId.set(v.id, v));
      freshComissoes.forEach(c => {
        if (c.vendedor && !perfilPorVendedorId.has(c.vendedor_id)) perfilPorVendedorId.set(c.vendedor_id, c.vendedor);
      });

      for (const [vendedorId, negs] of Object.entries(byVendedor)) {
        const existing = freshComissoes.find(c => c.vendedor_id === vendedorId);

        // A linha do pós-venda (só comissão de indicação) é montada depois deste laço.
        if (existing?.politica === POLITICA_POS_VENDA_INDICACAO) continue;

        // META/SALARY INHERITANCE:
        // 1. Use existing record's meta if > 0
        // 2. Else use previous month's meta
        // 3. Else default to 0
        let metaKwh = Number(existing?.meta_kwh) || 0;
        let salarioBase = Number(existing?.salario_base) || 0;
        if (metaKwh === 0 && salarioBase === 0 && previousMetas) {
          const prev = previousMetas.get(vendedorId);
          if (prev) {
            metaKwh = prev.meta_kwh;
            salarioBase = prev.salario_base;
          }
        }

        // Parte do pós-venda nas vendas indicadas por ele (#6236). Calculada mesmo quando a
        // linha do vendedor já está aprovada/paga e é pulada abaixo: a do pós-venda tem
        // ciclo próprio. Mesma conta do fechamento (funções puras), só o repasse é usado.
        if (negs.some((n: any) => n.pos_venda_id)) {
          if (ehComercialInterno(perfilPorVendedorId.get(vendedorId)) && isCompetenciaNovaPoliticaInterno(mes, ano)) {
            repassesPosVenda.push(...calcularComissoesInterno(negs as any, metaKwh, tarifa.valor).repassesPosVenda);
          } else if (novaPolitica) {
            repassesPosVenda.push(...calcularAtivacaoV2(negs as any, metaKwh).repassesPosVenda);
          }
        }

        // Skip based on status — use FRESH data from DB, not stale refs
        if (existing && !forceAll) {
          if (existing.status === 'pago') continue;
          if (skipApproved && existing.status === 'aprovado') continue;
        }

        const negsNormais = negs.filter(n => !n.is_grupo_a);
        const consumos = negsNormais.map((n: any) => Number(n.consumo_medio_kwh) || 0);
        const totalKwh = consumos.reduce((s: number, c: number) => s + c, 0);

        // Comercial Interno tem motor PRÓPRIO — não é uma variação do v1/v2 do
        // vendedor externo (% de tabela por desconto, não ativação R$/kWh por
        // atingimento). Checa ANTES de novaPolitica: nunca deve cair nos ramos
        // abaixo, nem o antigo nem o v2.
        //
        // isCompetenciaNovaPoliticaInterno protege o histórico: a Júlia tem
        // registros de fev–jun/2026 em status 'calculado' (nunca aprovados),
        // que o auto-cálculo recalcula toda vez que a tela abre naquele mês.
        // Sem essa virada, o motor novo reescreveria retroativamente meses em
        // que ela era legitimamente paga pelo modelo antigo.
        if (ehComercialInterno(perfilPorVendedorId.get(vendedorId)) && isCompetenciaNovaPoliticaInterno(mes, ano)) {
          const fechamentoInterno = await calcularFechamentoInternoV1({
            vendedorId,
            mes,
            ano,
            negs: negs as any,
            salarioBase,
            metaKwh,
            tarifaValor: tarifa.valor,
            tarifaId: tarifa.id,
          });

          const savedInterno = await api.folhaPagamento.upsertComissaoVendedor({
            ...fechamentoInterno.comissaoData,
            status: existing ? existing.status : 'calculado',
          } as any);

          await supabase.from('comissao_vendedor_negociacoes').delete().eq('comissao_vendedor_id', savedInterno.id);
          for (const det of fechamentoInterno.detalhes) {
            await api.folhaPagamento.upsertDetalheNegociacao({
              comissao_vendedor_id: savedInterno.id,
              negociacao_id: det.negociacao_id,
              consumo_medio_kwh: det.consumo_medio_kwh,
              // Bônus por negociação não existe aqui — o único bônus (SLA) é
              // fixo e mensal, já embutido em comissaoData.valor_bonus.
              valor_bonus: 0,
              is_grupo_a: det.is_grupo_a,
              grupo_a_status: det.grupo_a_status,
              // Reaproveita as colunas genéricas do vendedor externo com o
              // mesmo PAPEL (multiplicador da negociação / quanto ela rendeu),
              // não o mesmo cálculo — o detalhamento fino (base, 3 pilares,
              // bônus de prospecção) mora em comissao_vendedores.detalhe_calculo.
              multiplicador_final: det.mult_final,
              valor_ativacao: det.valor_comissao,
            } as any);
          }
          continue;
        }

        if (novaPolitica) {
          // Recorrência EM ANÁLISE (Bruno 13/07) — não calculada; bônus de safra entra no fechamento
          const fechamento = await calcularFechamentoExternoV2({
            vendedorId,
            mes,
            ano,
            negs: negs as any,
            redeParceiros: redesPorVendedor.get(vendedorId) || [],
            salarioBase,
            metaKwh,
            tarifaValor: tarifa.valor,
            tarifaId: tarifa.id,
          });

          const savedV2 = await api.folhaPagamento.upsertComissaoVendedor({
            ...fechamento.comissaoData,
            status: existing ? existing.status : 'calculado',
          } as any);

          await supabase.from('comissao_vendedor_negociacoes').delete().eq('comissao_vendedor_id', savedV2.id);
          for (const det of fechamento.detalhes) {
            await api.folhaPagamento.upsertDetalheNegociacao({
              comissao_vendedor_id: savedV2.id,
              ...det,
            } as any);
          }
          continue;
        }

        const calc = calcularComissaoVendedor(totalKwh, metaKwh, tarifa.valor, salarioBase, consumos);

        const comissaoData = {
          vendedor_id: vendedorId,
          mes, ano,
          total_kwh_vendido: totalKwh,
          meta_kwh: metaKwh,
          percentual_atingimento: calc.percentualAtingimento,
          percentual_comissao: calc.percentualComissao,
          tarifa_energia_valor: tarifa.valor,
          tarifa_energia_id: tarifa.id,
          valor_comissao_kwh: calc.valorComissaoKwh,
          valor_bonus: calc.valorBonus,
          salario_base: salarioBase,
          valor_total: calc.valorTotal,
          status: existing ? existing.status : 'calculado' as const,
        };

        const saved = await api.folhaPagamento.upsertComissaoVendedor(comissaoData);

        await supabase.from('comissao_vendedor_negociacoes').delete().eq('comissao_vendedor_id', saved.id);

        for (const neg of negs) {
          await api.folhaPagamento.upsertDetalheNegociacao({
            comissao_vendedor_id: saved.id,
            negociacao_id: neg.id,
            consumo_medio_kwh: Number(neg.consumo_medio_kwh) || 0,
            valor_bonus: neg.is_grupo_a ? 0 : getBonusVendedor(Number(neg.consumo_medio_kwh) || 0),
            is_grupo_a: neg.is_grupo_a,
            grupo_a_status: neg.is_grupo_a ? 'pendente_aprovacao_gerente' : undefined,
          });
        }
      }

      // Linha de cada pessoa do pós-venda com a parte das vendas que ela indicou (#6236):
      // sem salário, sem meta — só a comissão de indicação. Também refaz (zerando) quem já
      // tinha linha no mês e perdeu as vendas (ex.: aprovação desfeita).
      const porPosVenda = agruparRepassesPorPosVenda(repassesPosVenda);
      const idsPosVenda = new Set<string>([
        ...porPosVenda.keys(),
        ...freshComissoes.filter(c => c.politica === POLITICA_POS_VENDA_INDICACAO).map(c => c.vendedor_id),
      ]);
      for (const posVendaId of idsPosVenda) {
        const existing = freshComissoes.find(c => c.vendedor_id === posVendaId);
        // Já tem linha de vendedor no mês (mudou de papel): não sobrescreve.
        if (existing && existing.politica !== POLITICA_POS_VENDA_INDICACAO) continue;
        if (existing && !forceAll) {
          if (existing.status === 'pago') continue;
          if (skipApproved && existing.status === 'aprovado') continue;
        }
        const grupo = porPosVenda.get(posVendaId) || { total: 0, kwh: 0, itens: [] };
        const savedPosVenda = await api.folhaPagamento.upsertComissaoVendedor({
          vendedor_id: posVendaId,
          mes,
          ano,
          politica: POLITICA_POS_VENDA_INDICACAO,
          total_kwh_vendido: grupo.kwh,
          meta_kwh: 0,
          percentual_atingimento: 0,
          percentual_comissao: 0,
          tarifa_energia_valor: tarifa.valor,
          tarifa_energia_id: tarifa.id,
          valor_comissao_kwh: grupo.total,
          valor_bonus: 0,
          salario_base: 0,
          valor_ativacao: 0,
          valor_threshold: 0,
          valor_rede_parceiros: 0,
          valor_bonus_safra: 0,
          valor_recorrencia: 0,
          valor_clawback: 0,
          valor_total: grupo.total,
          detalhe_calculo: { indicacoes_pos_venda: grupo.itens },
          status: existing ? existing.status : 'calculado',
        } as any);
        await supabase.from('comissao_vendedor_negociacoes').delete().eq('comissao_vendedor_id', savedPosVenda.id);
        for (const item of grupo.itens) {
          await api.folhaPagamento.upsertDetalheNegociacao({
            comissao_vendedor_id: savedPosVenda.id,
            negociacao_id: item.negociacao_id,
            consumo_medio_kwh: item.kwh,
            valor_bonus: 0,
            is_grupo_a: false,
            // Mesma coluna que a linha do vendedor usa para "quanto a negociação rendeu".
            valor_ativacao: item.parte_pos_venda,
          } as any);
        }
      }

      onRefresh();
    } catch (err) {
      console.error('Calc error:', err);
    } finally {
      autoCalcRunningRef.current = false;
      setAutoCalcDone(true);
      setAutoCalcRunning(false);
    }
  }, [mes, ano]);

  // Auto-calc: runs on mount/month change (skips approved)
  // Only triggers when vendedores are loaded (prevents race condition)
  useEffect(() => {
    if (vendedores.length === 0) return;
    setAutoCalcDone(false);
    runCalc(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, ano, vendedores.length]);

  // Re-calc when metas/tarifas change from ConfiguracoesTab
  const prevFingerprintRef = useRef(configFingerprint);
  useEffect(() => {
    if (vendedores.length === 0) return;
    if (prevFingerprintRef.current !== configFingerprint) {
      prevFingerprintRef.current = configFingerprint;
      runCalc(true);
    }
  }, [configFingerprint, runCalc]);

  // Manual sync button handler (recalculates everything including paid, preserving status)
  const handleForceSync = async () => {
    addToast('Recalculando todas as comissões...', 'info');
    if (await runCalc(false, true) === 'bloqueado') {
      addToast('Comissões não recalculadas: não foi possível ler as indicações do pós-venda.', 'warning');
      return;
    }
    addToast('Comissões recalculadas com sucesso!', 'success');
    logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'folha_pagamento', acao: 'recalcular_comissoes', origem: 'acao' });
  };

  const handleExpand = async (comissao: ComissaoVendedor) => {
    if (expandedId === comissao.id) {
      setExpandedId(null);
      setDetalhe([]);
      return;
    }
    setExpandedId(comissao.id);
    setLoadingDetalhe(true);
    try {
      const data = await api.folhaPagamento.getDetalheNegociacoes(comissao.id);
      setDetalhe(data);
    } catch { setDetalhe([]); }
    finally { setLoadingDetalhe(false); }
  };

  const handleStatusChange = async (id: number, newStatus: 'aprovado' | 'pago') => {
    if (newStatus === 'aprovado') {
      const comissao = comissoes.find(c => c.id === id);
      if (comissao) {
        setApprovalModal({ comissao });
        return;
      }
    }
    // If marking as "pago", open the payment confirmation modal
    if (newStatus === 'pago') {
      const comissao = comissoes.find(c => c.id === id);
      if (comissao) {
        setPaymentModal({ comissao });
        return;
      }
    }
  };

  const handleConfirmApproval = async () => {
    if (!approvalModal) return;
    setUploadingApproval(true);
    try {
      await api.folhaPagamento.updateComissaoVendedor(approvalModal.comissao.id, {
        status: 'aprovado',
        aprovado_por: profile?.id,
        aprovado_em: new Date().toISOString(),
      } as any);
      addToast('Comissão aprovada com sucesso!', 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'folha_pagamento', acao: 'aprovar_comissao', origem: 'acao' });
      setApprovalModal(null);
      onRefresh();
    } catch {
      addToast('Erro ao aprovar comissão', 'error');
    } finally {
      setUploadingApproval(false);
    }
  };

  const handleConfirmReset = async () => {
    if (!resetModal) return;
    setResetting(true);
    try {
      await api.folhaPagamento.updateComissaoVendedor(resetModal.comissao.id, {
        status: 'calculado',
        aprovado_por: null,
        aprovado_em: null,
        pago_por: null,
        pago_em: null,
        comprovante_url: null,
        comprovante_nome: null,
      } as any);
      addToast('Status resetado para "Calculado" com sucesso.', 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'folha_pagamento', acao: 'resetar_status_comissao', origem: 'acao' });
      setResetModal(null);
      onRefresh();
    } catch {
      addToast('Erro ao resetar status', 'error');
    } finally {
      setResetting(false);
    }
  };

  // -- Payment Confirmation --
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      addToast('Arquivo muito grande (máx 5MB)', 'error');
      return;
    }
    setComprovanteFile(file);
    // Preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setComprovantePreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setComprovantePreview(null);
    }
  };

  const handleConfirmPayment = async () => {
    if (!paymentModal) return;
    if (!comprovanteFile) {
      addToast('O comprovante PIX é obrigatório', 'error');
      return;
    }
    setUploading(true);
    try {
      let comprovanteUrl = '';
      let comprovanteNome = '';

      if (comprovanteFile) {
        const ext = comprovanteFile.name.split('.').pop();
        const path = `vendedores/${paymentModal.comissao.id}_${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('comprovantes')
          .upload(path, comprovanteFile, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from('comprovantes').getPublicUrl(path);
        comprovanteUrl = urlData.publicUrl;
        comprovanteNome = comprovanteFile.name;
      }

      await api.folhaPagamento.updateComissaoVendedor(paymentModal.comissao.id, {
        status: 'pago',
        comprovante_url: comprovanteUrl || undefined,
        comprovante_nome: comprovanteNome || undefined,
        pago_por: profile?.id,
        pago_em: new Date().toISOString(),
      } as any);

      addToast('Pagamento confirmado com sucesso!', 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'folha_pagamento', acao: 'confirmar_pagamento_comissao', origem: 'acao' });
      setPaymentModal(null);
      setComprovanteFile(null);
      setComprovantePreview(null);
      onRefresh();
    } catch (err) {
      console.error(err);
      addToast('Erro ao confirmar pagamento', 'error');
    } finally { setUploading(false); }
  };

  // ── Stable sorted list — prevents random reorder on recalc ──
  const sortedComissoes = useMemo(
    () => [...comissoes].sort((a, b) =>
      (a.vendedor?.full_name || '').localeCompare(b.vendedor?.full_name || '', 'pt-BR')
    ),
    [comissoes]
  );

  // Nova Política Comercial: competência >= jul/2026 exibe os componentes v2
  const novaPolitica = isCompetenciaNovaPolitica(mes, ano);

  // Comissão do pós-venda por indicação (#6236): a linha só do pós-venda e o repasse de cada venda.
  const ehLinhaPosVenda = (c: ComissaoVendedor) => c.politica === POLITICA_POS_VENDA_INDICACAO;
  const repasseDaNegociacao = (c: ComissaoVendedor, negociacaoId: number): RepassePosVenda | undefined =>
    ((c.detalhe_calculo?.indicacoes_pos_venda || []) as RepassePosVenda[])
      .find(r => Number(r.negociacao_id) === Number(negociacaoId));
  const nomeUsuario = (id: string | null | undefined, padrao: string) =>
    (id && todosUsuarios.find(u => u.id === id)?.full_name) || padrao;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h3 className="font-bold text-lg text-gray-800 dark:text-white">Comissões de Vendedores</h3>
          {comissoes.length > 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{comissoes.length} vendedor(es) · {formatCurrency(comissoes.reduce((s, c) => s + c.valor_total, 0))} total</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {autoCalcRunning && (
            <div className="flex items-center gap-2 bg-[#F6A937]/10 px-3 py-1.5 rounded-lg">
              <RefreshCw size={13} className="animate-spin text-[#F6A937]" />
              <span className="text-[#b85c00] dark:text-[#F6A937] font-medium text-xs">Calculando...</span>
            </div>
          )}
          {canEdit && !autoCalcRunning && (
            <button
              onClick={handleForceSync}
              className="flex items-center gap-2 px-4 py-2 bg-[rgba(95,108,55,0.9)] text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all"
              title="Recalcula todas as comissões com as configurações atuais"
            >
              <RefreshCw size={14} />
              Recalcular
            </button>
          )}
        </div>
      </div>

      {/* Sem ler as indicações do pós-venda, a Folha não recalcula (#6236) */}
      {avisoIndicacoes && (
        <div className="flex items-start gap-2.5 p-3.5 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span><strong>As comissões não foram recalculadas.</strong> {avisoIndicacoes}</span>
        </div>
      )}

      {/* Table */}
      {comissoes.length === 0 && !autoCalcRunning ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-14 text-center" style={{ animation: 'fadeSlideUp 0.4s ease-out' }}>
          <Calculator size={40} className="mx-auto text-gray-200 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400 font-semibold text-sm">Nenhuma comissão encontrada neste período</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">As comissões são calculadas automaticamente quando há negociações com validação de dados aprovada.</p>
        </div>
      ) : comissoes.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden" style={{ animation: 'fadeSlideUp 0.4s ease-out' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/60">
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Vendedor</th>
                  <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Meta</th>
                  <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Vendido</th>
                  <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Ating.</th>
                  {novaPolitica ? (
                    <>
                      {MOSTRAR_ITENS_V2_EM_DISCUSSAO && (
                        <th className="text-center px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">SLA</th>
                      )}
                      <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Ativação</th>
                      <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Threshold</th>
                      <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Rede</th>
                      {MOSTRAR_ITENS_V2_EM_DISCUSSAO && (
                        <>
                          <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Safra 1ª fat.</th>
                          <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Clawback</th>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">%Com.</th>
                      <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Salário</th>
                      <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Comissão</th>
                      <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Bônus</th>
                    </>
                  )}
                  <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Total</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Status</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/40">
                {sortedComissoes.map((c, idx) => (
                  <React.Fragment key={c.id}>
                    <tr
                      className="hover:bg-gray-50/70 dark:hover:bg-gray-700/20 cursor-pointer transition-colors"
                      onClick={() => handleExpand(c)}
                      style={{ animation: `fadeSlideUp ${0.35 + idx * 0.05}s ease-out` }}
                    >
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-[rgba(95,108,55,0.9)] flex items-center justify-center text-[10px] font-black text-white flex-shrink-0 shadow-sm">
                            {(c.vendedor?.full_name || 'N').split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <span className="block font-semibold text-sm text-gray-900 dark:text-white">{c.vendedor?.full_name || 'N/A'}</span>
                            {ehLinhaPosVenda(c) && (
                              <span className="block text-[10px] font-bold uppercase tracking-wide text-violet-600 dark:text-violet-400">Pós-venda · comissão de indicação</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3.5 text-right text-gray-600 dark:text-gray-300 tabular-nums text-[13px]">{ehLinhaPosVenda(c) ? '—' : formatKwh(c.meta_kwh)}</td>
                      <td className="px-3 py-3.5 text-right text-gray-600 dark:text-gray-300 tabular-nums text-[13px]">{formatKwh(c.total_kwh_vendido)}</td>
                      <td className="px-3 py-3.5 text-right">
                        {ehLinhaPosVenda(c) ? <span className="text-gray-400 dark:text-gray-500">—</span> : (
                          <span className={`font-black tabular-nums ${c.percentual_atingimento >= 100 ? 'text-[#5F6C37] dark:text-[#84cc16]' : 'text-[#F6A937]'}`}>
                            {formatPercent(c.percentual_atingimento)}
                          </span>
                        )}
                      </td>
                      {novaPolitica ? (
                        <>
                          {MOSTRAR_ITENS_V2_EM_DISCUSSAO && (
                            <td className="px-3 py-3.5 text-center">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black ${(c as any).sla_cumprido ? 'bg-[#5F6C37]/10 dark:bg-[#5F6C37]/20 text-[#5F6C37] dark:text-[#84cc16]' : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'}`}>
                                {(c as any).sla_cumprido ? 'OK' : '½'}
                              </span>
                            </td>
                          )}
                          <td className="px-3 py-3.5 text-right text-[#5F6C37] dark:text-[#84cc16] font-semibold tabular-nums">{ehLinhaPosVenda(c) ? <span className="text-gray-400 dark:text-gray-500 font-normal">—</span> : formatCurrency(Number((c as any).valor_ativacao) || 0)}</td>
                          <td className="px-3 py-3.5 text-right text-gray-600 dark:text-gray-300 tabular-nums">{ehLinhaPosVenda(c) ? <span className="text-gray-400 dark:text-gray-500">—</span> : formatCurrency(Number((c as any).valor_threshold) || 0)}</td>
                          <td className="px-3 py-3.5 text-right text-gray-600 dark:text-gray-300 tabular-nums">{ehLinhaPosVenda(c) ? <span className="text-gray-400 dark:text-gray-500">—</span> : formatCurrency(Number((c as any).valor_rede_parceiros) || 0)}</td>
                          {MOSTRAR_ITENS_V2_EM_DISCUSSAO && (
                            <>
                              <td className="px-3 py-3.5 text-right text-[#F6A937] font-semibold tabular-nums">{formatCurrency(Number((c as any).valor_bonus_safra) || 0)}</td>
                              <td className={`px-3 py-3.5 text-right font-semibold tabular-nums ${(Number((c as any).valor_clawback) || 0) < 0 ? 'text-red-500' : 'text-gray-400 dark:text-gray-500'}`}>{formatCurrency(Number((c as any).valor_clawback) || 0)}</td>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-3.5 text-right">
                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-black bg-[#5F6C37]/10 dark:bg-[#5F6C37]/20 text-[#5F6C37] dark:text-[#84cc16]">{c.percentual_comissao}%</span>
                          </td>
                          <td className="px-3 py-3.5 text-right text-gray-600 dark:text-gray-300 tabular-nums">{formatCurrency(c.salario_base)}</td>
                          <td className="px-3 py-3.5 text-right text-[#5F6C37] dark:text-[#84cc16] font-semibold tabular-nums">{formatCurrency(c.valor_comissao_kwh)}</td>
                          <td className="px-3 py-3.5 text-right text-[#F6A937] font-semibold tabular-nums">{formatCurrency(c.valor_bonus)}</td>
                        </>
                      )}
                      <td className="px-3 py-3.5 text-right font-black text-gray-900 dark:text-white tabular-nums">{formatCurrency(c.valor_total)}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-bold ${STATUS_COLORS[c.status] || ''}`}>
                            {c.status === 'calculado' ? 'Calculado' : c.status === 'aprovado' ? 'Aprovado' : 'Pago'}
                          </span>
                          {/* Audit trail: who approved */}
                          {c.status !== 'calculado' && c.aprovado_por && (
                            <span className="text-[9px] text-gray-400 dark:text-gray-500" title={`Aprovado em ${c.aprovado_em ? new Date(c.aprovado_em).toLocaleString('pt-BR') : '?'}`}>
                              ✓ {todosUsuarios.find(u => u.id === c.aprovado_por)?.full_name?.split(' ')[0] || 'Usuário'}
                              {c.aprovado_em && ` · ${new Date(c.aprovado_em).toLocaleDateString('pt-BR')}`}
                            </span>
                          )}
                          {/* Audit trail: who paid */}
                          {c.status === 'pago' && c.pago_por && (
                            <span className="text-[9px] text-gray-400 dark:text-gray-500" title={`Pago em ${c.pago_em ? new Date(c.pago_em).toLocaleString('pt-BR') : '?'}`}>
                              $ {todosUsuarios.find(u => u.id === c.pago_por)?.full_name?.split(' ')[0] || 'Usuário'}
                              {c.pago_em && ` · ${new Date(c.pago_em).toLocaleDateString('pt-BR')}`}
                            </span>
                          )}
                          {c.status === 'pago' && c.comprovante_url && (
                            <a
                              href={c.comprovante_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="bg-gray-100 uppercase dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-[9px] px-2 py-1 rounded-full flex items-center gap-1 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                            >
                              <Paperclip size={10} /> Comprovante
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3.5">
                        <div className="flex items-center gap-1">
                          {canEdit && c.status === 'calculado' && (
                            <button onClick={(e) => { e.stopPropagation(); handleStatusChange(c.id, 'aprovado'); }} className="p-1.5 text-[#5F6C37] hover:bg-[#5F6C37]/10 dark:hover:bg-[#5F6C37]/20 rounded-lg transition-colors" title="Aprovar">
                              <CheckCircle2 size={16} />
                            </button>
                          )}
                          {canEdit && (c.status === 'calculado' || c.status === 'aprovado') && (
                            <button onClick={(e) => { e.stopPropagation(); handleStatusChange(c.id, 'pago'); }} className="p-1.5 text-[#5F6C37] hover:bg-[#5F6C37]/10 dark:hover:bg-[#5F6C37]/20 rounded-lg transition-colors" title="Confirmar Pagamento">
                              <DollarSign size={16} />
                            </button>
                          )}
                          {profile?.role === 'adm' && c.status !== 'calculado' && (
                            <button onClick={(e) => { e.stopPropagation(); setResetModal({ comissao: c }); }} className="p-1.5 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Resetar aprovação/pagamento">
                              <RotateCcw size={15} />
                            </button>
                          )}
                          {expandedId === c.id ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                        </div>
                      </td>
                    </tr>
                    {expandedId === c.id && (
                      <tr>
                        <td colSpan={novaPolitica ? (MOSTRAR_ITENS_V2_EM_DISCUSSAO ? 13 : 10) : 11} className="px-6 py-4 bg-gray-50/50 dark:bg-gray-800/50">
                          {novaPolitica && MOSTRAR_ITENS_V2_EM_DISCUSSAO && (c as any).sla_detalhe && (
                            <div className="mb-4 p-3.5 rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">SLA de esforço no CRM (Filtro 1) {(c as any).sla_cumprido ? '— cumprido' : '— não cumprido: ativação e threshold reduzidos pela metade'}</p>
                              <div className="flex flex-wrap gap-2">
                                {[
                                  { label: 'Visitas', v: (c as any).sla_detalhe.visitas, meta: 200 },
                                  { label: 'Pós-venda', v: (c as any).sla_detalhe.posVenda, meta: 5 },
                                  { label: 'Fechamentos', v: (c as any).sla_detalhe.fechamentos, meta: 5 },
                                  { label: 'Parceiros', v: (c as any).sla_detalhe.parceiros, meta: 2 },
                                ].map(item => (
                                  <span key={item.label} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border ${item.v >= item.meta ? 'text-[#5F6C37] dark:text-[#84cc16] bg-[#5F6C37]/5 border-[#5F6C37]/15' : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
                                    {item.label}: {item.v}/{item.meta}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {novaPolitica && MOSTRAR_ITENS_V2_EM_DISCUSSAO && (c as any).safra_detalhe && (
                            <div className="mb-4 p-3.5 rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                                Bônus de safra — 1ª fatura
                                <span className="ml-2 normal-case font-normal">
                                  (safra {String((c as any).safra_detalhe.safraMes).padStart(2, '0')}/{(c as any).safra_detalhe.safraAno} · {(c as any).safra_detalhe.pagosNoMes}/{(c as any).safra_detalhe.totalClientes} clientes pagaram no mês do 1º boleto)
                                </span>
                              </p>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border ${(c as any).safra_detalhe.pct >= 90 ? 'text-[#5F6C37] dark:text-[#84cc16] bg-[#5F6C37]/5 border-[#5F6C37]/15' : 'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-700'}`}>
                                  {(c as any).safra_detalhe.pct.toFixed(0)}% pagos no mês
                                </span>
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border text-[#F6A937] bg-[#F6A937]/10 border-[#F6A937]/20">
                                  Bônus: {formatCurrency(Number((c as any).valor_bonus_safra) || 0)}
                                </span>
                                <span className="text-[11px] text-gray-400 dark:text-gray-500">≥90% = R$ 250 · 100% = R$ 500</span>
                              </div>
                            </div>
                          )}
                          {loadingDetalhe ? (
                            <p className="text-sm text-gray-400">Carregando...</p>
                          ) : detalhe.length === 0 ? (
                            <p className="text-sm text-gray-400">Nenhuma negociação vinculada</p>
                          ) : (
                            <div className="space-y-2">
                              {(() => {
                                const detalheAprovados = detalhe.filter(d => {
                                  const decisao = (d.negociacao as any)?.analise_cadastral?.[0]?.decisao_final;
                                  return decisao === 'Aprovado' || decisao === 'Aprovado com Ressalvas';
                                });
                                if (detalheAprovados.length === 0) return <p className="text-sm text-gray-400">Nenhuma negociação aprovada vinculada</p>;
                                return (<>
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Negociações do mês ({detalheAprovados.length})</p>
                              {detalheAprovados.map(d => {
                                const neg = d.negociacao as any;
                                const ac = neg?.analise_cadastral?.[0];
                                const analistaName = ac?.analista?.full_name || null;
                                const dataAprovacao = (ac?.decisao_final_at || ac?.updated_at) ? new Date(ac.decisao_final_at || ac.updated_at).toLocaleDateString('pt-BR') : null;
                                const decisaoFinal = ac?.decisao_final || null;
                                const dataAssinatura = neg?.data_assinatura_termo
                                  ? new Date(neg.data_assinatura_termo).toLocaleDateString('pt-BR')
                                  : null;

                                return (
                                <div key={d.id} className={`p-3.5 rounded-xl border ${d.is_grupo_a ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10' : 'border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
                                  {/* Row 1: Title + kWh + Bonus */}
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-3 min-w-0">
                                      {d.is_grupo_a && <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />}
                                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {neg?.titulo || `#${d.negociacao_id}`}
                                      </span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {neg?.cliente_nome}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm flex-shrink-0">
                                      <span className="text-gray-600 dark:text-gray-300">{formatKwh(d.consumo_medio_kwh)}</span>
                                      {d.is_grupo_a ? (
                                        <span className="text-xs font-bold text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded">Grupo A — Cálculo Manual</span>
                                      ) : ehLinhaPosVenda(c) ? (
                                        <span className="text-violet-700 dark:text-violet-300 font-medium">
                                          Parte do pós-venda: {formatCurrency(Number((d as any).valor_ativacao) || 0)}
                                        </span>
                                      ) : (c as any).politica === 'interno_v1' ? (
                                        // Reaproveita as mesmas colunas do vendedor externo (multiplicador_final,
                                        // valor_ativacao), mas o rótulo "Ativação" não existe no Comercial
                                        // Interno — o número é a comissão da negociação, não uma taxa de ativação.
                                        <span className="text-secondary dark:text-amber-400 font-medium">
                                          {(d as any).multiplicador_final != null && (
                                            <span className="text-gray-500 dark:text-gray-400 mr-2">{Number((d as any).multiplicador_final).toFixed(2).replace('.', ',')}x</span>
                                          )}
                                          Comissão: {formatCurrency(Number((d as any).valor_ativacao) || 0)}
                                        </span>
                                      ) : novaPolitica ? (
                                        <span className="text-secondary dark:text-amber-400 font-medium">
                                          {(d as any).multiplicador_final != null && (
                                            <span className="text-gray-500 dark:text-gray-400 mr-2">{Number((d as any).multiplicador_final).toFixed(2).replace('.', ',')}x</span>
                                          )}
                                          Ativação: {formatCurrency(Number((d as any).valor_ativacao) || 0)}
                                        </span>
                                      ) : (
                                        <span className="text-secondary dark:text-amber-400 font-medium">Bônus: {formatCurrency(d.valor_bonus)}</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Row 2: Metadata chips + Action buttons */}
                                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700/60">
                                    {/* Data assinatura termo */}
                                    {dataAssinatura && (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-[11px] text-gray-600 dark:text-gray-300 border border-gray-100 dark:border-gray-600">
                                        <FileText size={10} className="text-gray-400" />
                                        Termo: <strong>{dataAssinatura}</strong>
                                      </span>
                                    )}

                                    {/* Data aprovação análise */}
                                    {dataAprovacao && (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#5F6C37]/5 dark:bg-[#5F6C37]/10 text-[11px] text-[#5F6C37] dark:text-[#84cc16] border border-[#5F6C37]/15 dark:border-[#5F6C37]/20">
                                        <CheckCircle2 size={10} />
                                        {decisaoFinal || 'Aprovado'}: <strong>{dataAprovacao}</strong>
                                        {analistaName && <span className="text-gray-400 dark:text-gray-500"> por {analistaName}</span>}
                                      </span>
                                    )}

                                    {/* Venda indicada pelo pós-venda (#6236): 120% da normal, metade para cada lado */}
                                    {(() => {
                                      const repasse = repasseDaNegociacao(c, d.negociacao_id);
                                      if (!repasse) return null;
                                      const quem = ehLinhaPosVenda(c)
                                        ? `Venda de ${nomeUsuario(neg?.responsavel_id, 'vendedor')}`
                                        : `Indicação de ${nomeUsuario(repasse.pos_venda_id, 'pós-venda')} (pós-venda)`;
                                      return (
                                        <span
                                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-violet-50 dark:bg-violet-900/20 text-[11px] text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-800"
                                          title="Venda indicada pelo pós-venda: a comissão vale 120% da normal e é dividida meio a meio entre quem vendeu e o pós-venda."
                                        >
                                          <Users size={10} />
                                          {quem} · normal {formatCurrency(repasse.valor_normal)} → 120% {formatCurrency(repasse.valor_total)}, metade para cada
                                        </span>
                                      );
                                    })()}

                                    {/* Spacer */}
                                    <div className="flex-1" />

                                    {/* Action: Open Analysis */}
                                    <a
                                      href={`/#/gestao?negociacaoId=${d.negociacao_id}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-[#5F6C37] bg-[#5F6C37]/8 hover:bg-[#5F6C37]/15 dark:text-[#84cc16] dark:bg-[#5F6C37]/15 dark:hover:bg-[#5F6C37]/25 border border-[#5F6C37]/15 dark:border-[#5F6C37]/25 transition-colors"
                                      title="Abrir validação de dados"
                                    >
                                      <ClipboardCheck size={12} />
                                      Abrir Análise
                                    </a>

                                    {/* Action: Open in CRM */}
                                    <a
                                      href={`/#/crm?negociacaoId=${d.negociacao_id}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 dark:text-gray-300 dark:bg-gray-700/50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 transition-colors"
                                      title="Abrir negociação no CRM"
                                    >
                                      <ExternalLink size={12} />
                                      Abrir no CRM
                                    </a>
                                  </div>
                                </div>
                                );
                              })}
                              </>);
                              })()}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Approval Modal Portal */}
      {approvalModal && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-0" style={{ animation: 'modalBackdropIn 0.25s ease-out' }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setApprovalModal(null)} />
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative z-10 border border-gray-100 dark:border-gray-700" style={{ animation: 'modalSlideIn 0.3s cubic-bezier(0.16,1,0.3,1)' }} onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="w-16 h-16 bg-[rgba(95,108,55,0.1)] dark:bg-[rgba(95,108,55,0.2)] text-[rgba(95,108,55,0.9)] dark:text-[#84cc16] rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white dark:border-gray-800 shadow-sm">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="text-xl font-bold text-center text-gray-900 dark:text-white mb-2">Aprovar Comissão?</h3>
              <p className="text-center text-sm text-gray-600 dark:text-gray-300 mb-6 px-4">
                Tem certeza que deseja aprovar as comissões de <strong className="text-gray-900 dark:text-white">{vendedores.find(v => v.id === approvalModal.comissao.vendedor_id)?.full_name || approvalModal.comissao.vendedor?.full_name || 'Vendedor'}</strong> no valor de <strong className="text-gray-900 dark:text-white">{formatCurrency(approvalModal.comissao.valor_total)}</strong>?
                <br /><br />
                <span className="text-xs text-gray-500 block bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">Esta é uma ação que confirma os valores perante o colaborador.</span>
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setApprovalModal(null)}
                  className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmApproval}
                  disabled={uploadingApproval}
                  className="flex-1 px-4 py-3 bg-[rgba(95,108,55,0.9)] hover:bg-[rgba(77,90,44,0.9)] text-white rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm transition-colors"
                >
                  {uploadingApproval ? (
                    <><RefreshCw size={16} className="animate-spin" /> Processando...</>
                  ) : (
                    'Sim, Aprovar'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Payment Confirmation Modal Portal */}
      {paymentModal && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-0" style={{ animation: 'modalBackdropIn 0.25s ease-out' }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setPaymentModal(null); setComprovanteFile(null); setComprovantePreview(null); }} />
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative z-10 border border-gray-100 dark:border-gray-700 flex flex-col max-h-[90vh]" style={{ animation: 'modalSlideIn 0.3s cubic-bezier(0.16,1,0.3,1)' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[rgba(95,108,55,0.1)] dark:bg-[rgba(95,108,55,0.2)] text-[rgba(95,108,55,0.9)] dark:text-[#84cc16] flex items-center justify-center">
                  <DollarSign size={18} strokeWidth={2.5} />
                </div>
                Registrar Pagamento
              </h3>
              <button onClick={() => { setPaymentModal(null); setComprovanteFile(null); setComprovantePreview(null); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1.5 hover:bg-white dark:hover:bg-gray-700 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 flex-1 overflow-y-auto">
              {/* Valor details */}
              <div className="bg-[#212913] p-5 rounded-xl mb-6 flex flex-col items-center justify-center text-center">
                <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-1">Valor a Transferir</p>
                <p className="text-3xl font-black text-white tabular-nums">
                  {formatCurrency(paymentModal.comissao.valor_total)}
                </p>
                <div className="mt-2 px-3 py-1 bg-white/10 rounded-full inline-flex text-xs font-medium text-white/70">
                  {vendedores.find(v => v.id === paymentModal.comissao.vendedor_id)?.full_name || paymentModal.comissao.vendedor?.full_name || 'Desconhecido'}
                </div>
              </div>

              {/* Upload Comprovante */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Comprovante PIX <span className="text-red-500">*</span>
                </label>

                {!comprovanteFile ? (
                  <label className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer hover:border-primary dark:hover:border-lime-500 hover:bg-primary/5 transition-all group">
                    <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center group-hover:bg-primary/10 dark:group-hover:bg-lime-900/20 transition-colors">
                      <Upload size={22} className="text-gray-400 group-hover:text-primary dark:group-hover:text-lime-400 transition-colors" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Arraste ou clique para anexar</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">PNG, JPG ou PDF — máx 5MB</p>
                    </div>
                    <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleFileSelect} />
                  </label>
                ) : (
                  <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {comprovantePreview ? (
                        <img src={comprovantePreview} alt="" className="w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-gray-600" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                          <FileText size={20} className="text-red-500" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[200px]">{comprovanteFile.name}</p>
                        <p className="text-xs text-gray-400">{(comprovanteFile.size / 1024).toFixed(0)} KB</p>
                      </div>
                    </div>
                    <button onClick={() => { setComprovanteFile(null); setComprovantePreview(null); }} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => { setPaymentModal(null); setComprovanteFile(null); setComprovantePreview(null); }}
                  className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmPayment}
                  disabled={uploading}
                  className="flex-1 px-4 py-3 bg-[rgba(95,108,55,0.9)] text-white rounded-xl text-sm font-bold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <><RefreshCw size={14} className="animate-spin" /> Enviando...</>
                  ) : (
                    <><CheckCircle2 size={14} /> Confirmar Pagamento</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Reset Modal Portal */}
      {resetModal && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-0" style={{ animation: 'modalBackdropIn 0.25s ease-out' }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setResetModal(null)} />
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative z-10 border border-gray-100 dark:border-gray-700" style={{ animation: 'modalSlideIn 0.3s cubic-bezier(0.16,1,0.3,1)' }} onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white dark:border-gray-800 shadow-sm">
                <RotateCcw size={28} />
              </div>
              <h3 className="text-xl font-bold text-center text-gray-900 dark:text-white mb-2">Resetar Status?</h3>
              <p className="text-center text-sm text-gray-600 dark:text-gray-300 mb-2 px-4">
                Isso irá reverter a comissão de <strong className="text-gray-900 dark:text-white">{todosUsuarios.find(u => u.id === resetModal.comissao.vendedor_id)?.full_name || 'Vendedor'}</strong> para o status <strong className="text-gray-900 dark:text-white">Calculado</strong>, removendo os registros de aprovação e pagamento.
              </p>
              <p className="text-center text-xs text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg p-3 mb-6">
                Status atual: <strong>{resetModal.comissao.status === 'aprovado' ? 'Aprovado' : 'Pago'}</strong>. Esta ação não pode ser desfeita automaticamente.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setResetModal(null)}
                  className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmReset}
                  disabled={resetting}
                  className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm transition-colors"
                >
                  {resetting ? (
                    <><RefreshCw size={16} className="animate-spin" /> Processando...</>
                  ) : (
                    <><RotateCcw size={15} /> Sim, Resetar</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes modalBackdropIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalSlideIn {
          from { opacity: 0; transform: scale(0.92) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default VendedoresTab;
